/**
 * Shared timesheet cost-code (billing category) helpers.
 *
 * Cost codes are firm-wide and configured in the billing_categories table (Team →
 * Company Rates → Cost Codes). The timesheet grids fetch them at load time and fall back to the
 * historical defaults below when a tenant has none yet (empty list or a failed
 * fetch), so a grid is never left with zero category rows.
 */
import type { BillingCategory } from "../Models/Rate/RateModels";

export interface TimesheetCategoryDef {
  code: string;
  description: string;
}

/**
 * The Field Services cost code bills at the employee's field rate instead of the
 * standard billed rate (backend services/Financial/time_entry_service.py). It is a
 * reserved code: it can be renamed but not deleted.
 */
export const FIELD_SERVICES_CATEGORY_CODE = "900";

/** Historical default cost codes, used only as a fallback when none are configured. */
export const DEFAULT_TIMESHEET_CATEGORIES: ReadonlyArray<TimesheetCategoryDef> = [
  { code: "100", description: "Mechanical Drawing Package Development" },
  { code: "110", description: "Mechanical Model Development" },
  { code: "200", description: "Civil-Structural Engineering & Design" },
  { code: "210", description: "Structural Model Development" },
  { code: "300", description: "Electrical Drawing Package Development" },
  { code: "400", description: "Instrumentation Base Scope" },
  { code: "500", description: "Mechanical/ Project Engineering & Design" },
  { code: "600", description: "Project administration" },
  { code: "900", description: "Field Services" },
];

/** Map raw billing_categories rows to the grid's {code, description} shape, sorted by code. */
export function mapBillingCategories(
  raw: ReadonlyArray<Pick<BillingCategory, "categoryid" | "category">> | null | undefined,
): TimesheetCategoryDef[] {
  return (raw || [])
    .filter((c) => c && c.categoryid != null && String(c.categoryid) !== "")
    .map((c) => ({ code: String(c.categoryid), description: c.category || String(c.categoryid) }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Resolve the configured cost codes, falling back to the defaults when empty.
 *
 * The reserved Field Services (900) row is always guaranteed to be present: it bills at the
 * employee's field rate, so if it were missing from a tenant's configured set, any time logged
 * under a "900 - …" note would have no matching grid row and silently drop out of the totals.
 */
export function resolveTimesheetCategories(
  raw: ReadonlyArray<Pick<BillingCategory, "categoryid" | "category">> | null | undefined,
): TimesheetCategoryDef[] {
  const mapped = mapBillingCategories(raw);
  const resolved = mapped.length > 0 ? mapped : [...DEFAULT_TIMESHEET_CATEGORIES];
  if (!resolved.some((c) => c.code === FIELD_SERVICES_CATEGORY_CODE)) {
    resolved.push({ code: FIELD_SERVICES_CATEGORY_CODE, description: "Field Services" });
    resolved.sort((a, b) => a.code.localeCompare(b.code));
  }
  return resolved;
}
